import { observer } from "mobx-react";
import { FreetextForm, FreetextTypeSelection } from "./freetextDomain";
import { YesNoQuestion } from "../common/commonComponents";
import { FC, Fragment, useEffect } from "react";
import { Box, Grid, VBox } from "../../components-v2/Box";
import { Select } from "../../components-v2/Select";
import { DocumentItem } from "../common/commonDomain";
import { useTranslation } from "../../stores/RootStoreContext";
import { RadioFieldQuestion } from "../../common/commonComponents";
import { LocalizedTextEditor } from "../../text/LocalizedTextEditor";

const Descriptions: FC<{ form: FreetextForm; documentId?: string }> = observer(
  ({ form, documentId }) => {
    return (
      <>
        {form.descriptionTexts
          .filter((d) => !documentId || documentId === d.document.documentId)
          .map(({ document, textModel }) => (
            <LocalizedTextEditor
              key={`${form.decisionId}_${document.documentId}`}
              model={textModel}
            />
          ))}
      </>
    );
  },
);

const FreetextTypeView: FC<{ form: FreetextForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  return form.freetextTypeModel !== undefined ? (
    <VBox>
      <RadioFieldQuestion
        question={{ key: "decisions.freetext.freetextType" }}
        fieldName={"freetextType"}
        field={form.freetextTypeModel}
        isLocked={form.isProjectLocked}
      />
      {form.isCustomized && (
        <Box justifyContent={"end"}>
          <Grid inline cols={2} alignItems={"center"}>
            {form.documents.map((d) => (
              <Fragment key={d.documentId}>
                <Box grow textAlign={"right"} justifyContent={"end"}>
                  {t(d.title)}:
                </Box>
                <Box>
                  <Select<FreetextTypeSelection>
                    items={form.freetextTypeOptions}
                    onSelect={(selected) =>
                      form.selectCustomizedFreetextTypeForDocument(selected.type, d.type)
                    }
                    idFn={(item) => item.type}
                    labelFn={(item) => t(item.label)}
                    value={form.getSelectedFreetextTypeForDocument(d.type)}
                    disabled={form.isProjectLocked}
                  />
                </Box>
              </Fragment>
            ))}
          </Grid>
          <hr />
        </Box>
      )}
    </VBox>
  ) : null;
});

export const FreetextView = observer(function FreetextView({
  form,
  documentContext,
}: {
  form: FreetextForm;
  documentContext: DocumentItem | undefined;
}) {
  const { t } = useTranslation();

  useEffect(() => form.init(), [form]);

  return (
    <VBox gap={"md"}>
      <LocalizedTextEditor model={form.localizedTitles} inputType="string" autofocus />
      {form.showFreetextType && <FreetextTypeView form={form} />}
      {form.showRequiresGeneralMeeting(documentContext) && (
        <YesNoQuestion
          question={t("decisions.freetext.requiresGM")}
          field={form.requiresGeneralMeeting}
          onChange={(e) => form.setRequiresGeneralMeeting(e)}
          isLocked={form.isProjectLocked || form.isGeneralMeetingMinutesLocked}
        />
      )}
      <Descriptions form={form} documentId={documentContext?.documentId} />
    </VBox>
  );
});
